/**
 * Ephemeral slice that coordinates the session restore lifecycle.
 */

import type { AppSessionSnapshot } from "../../../services/sessionRestore/sessionRestore.types";
import { defineEphemeralSlice } from "../runtime/defineEphemeralSlice";

/**
 * State driving the session restore loader and contributor orchestration.
 */
export interface AppSessionRestoreState {

    /** Whether a restore is currently in progress. */
    "isRestoring": boolean;

    /** Snapshot currently being restored, or `null` when idle. */
    "pendingSnapshot": AppSessionSnapshot | null;

    /** Contributor keys that have already been restored. */
    "restoredKeys": readonly string[];

    /** When `true`, the next itinerary search effect run must be skipped. */
    "skipNextItinerarySearch": boolean;

    /**
     * Timestamp (ms) of the most recent completed map camera restore. Map auto-fit
     * effects use this to avoid overwriting the restored camera immediately after it
     * has been applied.
     */
    "mapRestoredAt": number | null;
}

/** Default state: idle, no snapshot, no restored keys. */
export const APP_SESSION_RESTORE_DEFAULT: AppSessionRestoreState = {
    "isRestoring":             false,
    "pendingSnapshot":         null,
    "restoredKeys":            [],
    "skipNextItinerarySearch": false,
    "mapRestoredAt":           null,
};

/**
 * Slice used by `SessionRestoreBootstrap` and `useSessionRestoreContributor`
 * to coordinate the restore lifecycle without prop drilling.
 */
export const appSessionRestoreSlice = defineEphemeralSlice<AppSessionRestoreState>( {
    "key":     "appSessionRestore",
    "default": () => APP_SESSION_RESTORE_DEFAULT,
} );
